import { useEffect } from 'react';
import { useLocation } from 'react-use';

import { useSiteStore } from '../siteStore';

const SITE_PATH_SEGMENTS = ['sites', 'site'];
const SITE_ID_QUERY_PARAM = 'site';

type SiteId = string;

function doesSiteIdSeemToBeValid(siteId: string | null): boolean {
  /**  NOTE: this is not actual validation function - we don't check with BE if the site with such an ID actually exists
   * or if even can exist (for example we don't check the upper limit for the number, floating point is also OK etc.).
   * So the only thing we do here is filtering out some total gibberish, just to prevent the most obvious errors.
   */
  const parsed = Number(siteId);
  return !Number.isNaN(parsed) && parsed > 0;
}

function findSiteIdInPath(pathname: string): SiteId | null {
  const pathParts = pathname.split('/');
  const siteSegmentIndex = pathParts.findIndex((pathPart) => SITE_PATH_SEGMENTS.includes(pathPart));

  if (siteSegmentIndex === -1 || siteSegmentIndex === pathParts.length - 1) {
    return null;
  }

  const siteIdSegment = pathParts[siteSegmentIndex + 1];

  if (!doesSiteIdSeemToBeValid(siteIdSegment)) {
    return null;
  }

  return siteIdSegment;
}

function findSiteIdInQueryParams(queryParams: URLSearchParams): SiteId | null {
  const siteIdParam = queryParams.get(SITE_ID_QUERY_PARAM);

  if (!doesSiteIdSeemToBeValid(siteIdParam)) {
    return null;
  }

  return siteIdParam;
}

export const useCurrentSiteId = (): SiteId | null =>
  useSiteStore((state) =>
    state.preferredCurrentSiteId ? String(state.preferredCurrentSiteId) : null,
  );

// It fixes the discrepancy between siteId coming from url and localStorage
export const useSiteIdSynchronization = (): void => {
  const location = useLocation();
  const { preferredCurrentSiteId, setPreferredCurrentSiteId } = useSiteStore((state) => ({
    preferredCurrentSiteId: state.preferredCurrentSiteId
      ? String(state.preferredCurrentSiteId)
      : null,
    setPreferredCurrentSiteId: state.setPreferredCurrentSiteId,
  }));

  let siteId: SiteId | null = null;

  if (typeof location.href !== 'undefined') {
    const url = new URL(location.href);
    siteId =
      findSiteIdInPath(url.pathname) ??
      findSiteIdInQueryParams(url.searchParams) ??
      preferredCurrentSiteId;
  }

  useEffect(() => {
    if (!siteId || siteId === preferredCurrentSiteId) return;

    setPreferredCurrentSiteId(parseInt(siteId, 10));
  }, [setPreferredCurrentSiteId, siteId, preferredCurrentSiteId]);
};
